/**
 * useRoomStores - Composing hook for backward compatibility
 *
 * This hook provides a unified interface similar to the original useSupabaseRoomStore,
 * but uses the new split stores internally.
 *
 * NEW CODE SHOULD USE THE INDIVIDUAL STORES DIRECTLY:
 * - useRoomStore: Room connection
 * - useQueueStore: Queue management
 * - usePlaybackStore: Current song, cursor, pitch
 * - useSearchStore: Song search (database + YouTube)
 * - useProcessingStore: Processing jobs
 * - useEmojiStore: Emoji reactions
 *
 * Each store also exports granular selector hooks for optimal re-render performance.
 */

import useRoomStore, { useRoomConnection, useRoomInfo } from './roomStore'
import useQueueStore, { useQueue, useQueueHelpers } from './queueStore'
import usePlaybackStore, { useCurrentSong, usePitchShift, useCursor, usePlaybackState, usePreloadedSongData, useIsPlaying, usePlaybackVersion, usePlaybackRevision } from './playbackStore'
import useSearchStore, { useSearchResults, useYouTubeSearch, useSongs } from './searchStore'
import useProcessingStore, { useProcessingJobs, useProcessingJobsState, useFailedIngestions } from './processingStore'
import useEmojiStore, { useEmojiEvents } from './emojiStore'

/**
 * Combined hook for backward compatibility
 * Returns all state and actions from all stores
 *
 * @deprecated Use individual stores for better performance
 */
export const useRoomStores = () => {
  const roomStore = useRoomStore()
  const queueStore = useQueueStore()
  const playbackStore = usePlaybackStore()
  const searchStore = useSearchStore()
  const processingStore = useProcessingStore()
  const emojiStore = useEmojiStore()

  const getPreviousSongFromQueue = (queue, cursor) => {
    const items = (queue || queueStore.queue)
      .filter(item => typeof item.position === 'number' && item.position < cursor)
      .sort((a, b) => b.position - a.position)
    return items[0] || null
  }

  const getCurrentSongFromQueue = (queue, cursor) => {
    return (queue || queueStore.queue).find(item => item.position === cursor) || null
  }

  const getNextSongFromQueue = (queue, cursor) => {
    const items = (queue || queueStore.queue)
      .filter(item => typeof item.position === 'number' && item.position > cursor)
      .sort((a, b) => a.position - b.position)
    return items[0] || null
  }

  return {
    // Room state
    room: roomStore.room,
    roomCode: roomStore.roomCode,
    roomId: roomStore.roomId,
    isConnected: roomStore.isConnected,
    connectionStatus: roomStore.connectionStatus,

    // Playback state
    currentSong: playbackStore.currentSong,
    pitchShift: playbackStore.pitchShift,
    cursor: playbackStore.cursor,
    skipInProgress: playbackStore.skipInProgress,
    songDataCache: playbackStore.songDataCache,
    preloadedSongData: playbackStore.preloadedSongData,
    playbackRevision: playbackStore.playbackRevision,
    authoritativeIsPlaying: playbackStore.authoritativeIsPlaying,
    authoritativePosSeconds: playbackStore.authoritativePosSeconds,
    authoritativeAnchorPosSec: playbackStore.authoritativeAnchorPosSec,
    authoritativeAnchorServerMs: playbackStore.authoritativeAnchorServerMs,

    // Queue state
    queue: queueStore.queue,

    // Search state
    songs: searchStore.songs,
    searchResults: searchStore.searchResults,
    isSearching: searchStore.isSearching,
    youtubeSearchResults: searchStore.youtubeSearchResults,
    isSearchingYouTube: searchStore.isSearchingYouTube,
    hasSearchedYouTube: searchStore.hasSearchedYouTube,

    // Processing state
    processingJobs: processingStore.processingJobs,
    jobsFilter: processingStore.jobsFilter,
    jobsLoaded: processingStore.jobsLoaded,
    jobsLoading: processingStore.jobsLoading,
    failedIngestions: processingStore.failedIngestions,
    failedIngestionsLoaded: processingStore.failedIngestionsLoaded,
    failedIngestionsLoading: processingStore.failedIngestionsLoading,

    // Local UI state
    isPlaying: playbackStore.isPlaying,
    setIsPlaying: playbackStore.setIsPlaying,
    playbackVersion: playbackStore.playbackVersion,

    // Emoji state
    emojiEvents: emojiStore.emojiEvents,

    // Room actions
    createRoom: roomStore.createRoom,
    validateRoom: roomStore.validateRoom,
    joinRoom: roomStore.joinRoom,
    connectRealtime: roomStore.connectRealtime,
    disconnectRealtime: roomStore.disconnectRealtime,
    refreshRoomState: roomStore.refreshRoomState,
    manualRefresh: roomStore.manualRefresh,
    forceReconnection: roomStore.forceReconnection,
    getConnectionHealth: roomStore.getConnectionHealth,
    enableAutoReconnect: roomStore.enableAutoReconnect,
    disableAutoReconnect: roomStore.disableAutoReconnect,
    isAutoReconnectEnabled: roomStore.isAutoReconnectEnabled,
    getDebugInfo: roomStore.getDebugInfo,
    reset: roomStore.reset,

    // Playback actions
    loadCurrentSong: playbackStore.loadCurrentSong,
    play: (posSeconds = null) => playbackStore.play(roomStore.roomId, posSeconds),
    pause: (posSeconds = null) => playbackStore.pause(roomStore.roomId, posSeconds),
    seek: (posSeconds, isPlaying = null) => playbackStore.seek(roomStore.roomId, posSeconds, isPlaying),
    next: () => playbackStore.next(roomStore.roomId, queueStore.queue, getNextSongFromQueue),
    previous: () => playbackStore.previous(roomStore.roomId, queueStore.queue, getPreviousSongFromQueue),
    songEnded: (endedQueueItemId = null) => playbackStore.songEnded(roomStore.roomId, endedQueueItemId),
    adjustPitch: (pitch) => playbackStore.adjustPitch(roomStore.roomId, pitch),
    setAuthoritativePlayback: playbackStore.setAuthoritativePlayback,
    setPlaybackRevision: playbackStore.setPlaybackRevision,
    getSongData: playbackStore.getSongData,
    clearSongDataCache: playbackStore.clearSongDataCache,
    invalidateSongData: playbackStore.invalidateSongData,
    clearPlaybackIfQueueExhausted: (cursor) => playbackStore.clearPlaybackIfQueueExhausted(queueStore.queue, cursor),

    // Queue helper functions
    getPreviousSong: getPreviousSongFromQueue,
    getCurrentSong: getCurrentSongFromQueue,
    getNextSong: getNextSongFromQueue,

    // Queue actions
    addToQueue: (songId, addedBy = 'anonymous') =>
      queueStore.addToQueue(roomStore.roomCode, roomStore.roomId, songId, addedBy, {
        songs: searchStore.songs,
        searchResults: searchStore.searchResults
      }),
    removeFromQueue: (itemId) => queueStore.removeFromQueue(roomStore.roomCode, itemId),
    reorderQueue: (items) => queueStore.reorderQueue(roomStore.roomCode, items),

    // Search actions
    searchSongs: searchStore.searchSongs,
    searchYouTube: searchStore.searchYouTube,
    resetYouTubeSearch: searchStore.resetYouTubeSearch,
    submitYouTubeUrl: (url, addedBy = 'anonymous') =>
      searchStore.submitYouTubeUrl(url, addedBy, roomStore.roomCode, (job) => {
        processingStore.addProcessingJob(job)
      }),
    loadSongs: searchStore.loadSongs,

    // Processing actions
    loadProcessingJobs: (forceReload = false) => processingStore.loadProcessingJobs(roomStore.roomId, forceReload),
    setJobsFilter: processingStore.setJobsFilter,
    getFilteredJobs: processingStore.getFilteredJobs,
    getActiveJobsCount: processingStore.getActiveJobsCount,
    getJobsStats: processingStore.getJobsStats,
    retryProcessingJob: processingStore.retryProcessingJob,
    cleanupOldJobs: processingStore.cleanupOldJobs,
    updateProcessingJob: processingStore.updateProcessingJob,
    removeFromProcessing: processingStore.removeFromProcessing,

    // Failed ingestions
    loadFailedIngestions: processingStore.loadFailedIngestions,
    acknowledgeFailedIngestion: processingStore.acknowledgeFailedIngestion,
    deleteFailedIngestion: processingStore.deleteFailedIngestion,
    getFailedIngestionsStats: processingStore.getFailedIngestionsStats,

    // Emoji actions
    sendEmojiReaction: (emoji) => emojiStore.sendEmojiReaction(roomStore.roomCode, emoji)
  }
}

// Re-export all individual stores and their selectors for granular usage
export {
  useRoomStore,
  useRoomConnection,
  useRoomInfo,

  useQueueStore,
  useQueue,
  useQueueHelpers,

  usePlaybackStore,
  useCurrentSong,
  usePitchShift,
  useCursor,
  usePlaybackState,
  usePreloadedSongData,
  useIsPlaying,
  usePlaybackVersion,
  usePlaybackRevision,

  useSearchStore,
  useSearchResults,
  useYouTubeSearch,
  useSongs,

  useProcessingStore,
  useProcessingJobs,
  useProcessingJobsState,
  useFailedIngestions,

  useEmojiStore,
  useEmojiEvents
}

// Default export for backward compatibility
export default useRoomStores
